/**
 * ListEventAttendees rows merged into people, and the page walk that feeds them.
 *
 * The service returns one row per campaign membership. With `includeSubCampaigns` the event's own
 * SFDC campaign is listed first, then every campaign beneath it, so someone in three of them comes
 * back as three rows, possibly on different pages. The Attendance List shows people, so the rows
 * are merged here as they arrive (AWSECEventHub CR-317896698, "ListEventAttendees: sub-campaign
 * listing (UI integration)"):
 *
 *   - Two rows are one person when they share a `contactId` or a `leadId`. A later row can join
 *     two people already listed: P1 known only by lead L, P2 only by contact C, then a row with
 *     both (the lead converted). P1 and P2 become one, in the earlier one's place.
 *   - Never on `objectId`: it is the contact id when there is one, else the lead id, so it cannot
 *     connect a lead-only row to its converted contact's row. A row with neither id is its own
 *     person.
 *   - Memberships are unioned by `campaignMemberId`, nominations by `nominationId`.
 *   - Person fields (name, email, phone, company, …) come from the first row seen. The event's own
 *     campaign is listed first, so whenever the person is in it, that row wins.
 *   - The preferred nomination is recomputed over the union with the service's rule
 *     ({@link preferredNomination}); each row's own was chosen from that row's matches only.
 *
 * Only Attended and Registered members are listed (PM rule, 2026-10-30): a row with any other
 * campaign member status (Prospect, Sent, On-demand, …) is dropped before merging, so it neither
 * adds a person nor links two. A person's status is Attended when any of their memberships is,
 * else Registered ({@link listedMemberStatus}). A nomination whose only member row was dropped is
 * then unmatched, and lists as a nomination-only row.
 *
 * With the flag off each row has one membership and nothing merges (bar a lead and its converted
 * contact both in the one campaign, which are one person anyway), so both modes take this path.
 * Before the service change ships, rows carry no `memberships`/`nominations`/`contactId`/`leadId`;
 * the merged row then falls back to the row's own values.
 */
import type { CampaignMembership, EventAttendee, Nomination } from '../client/EventCalendarClient';

export type ListedMemberStatus = 'Attended' | 'Registered';

/**
 * The campaign member status the Attendance List lists, canonically spelled — trimmed and
 * case-insensitive, since SFDC statuses are free text — or undefined for any other status.
 */
export function listedMemberStatus(status: string | undefined): ListedMemberStatus | undefined {
  const k = status?.trim().toLowerCase();
  if (k === 'attended') return 'Attended';
  if (k === 'registered') return 'Registered';
  return undefined;
}

interface Person {
  /** First-seen order: the display order, and the key a merge keeps. */
  seq: number;
  /** The first row seen — the source of the person fields. */
  row: EventAttendee;
  keys: Set<string>;
  memberships: Map<string, CampaignMembership>;
  nominations: Map<string, Nomination>;
}

/** The row's nominations: `nominations` once the service sends it, else its one `nomination`. */
function rowNominations(row: EventAttendee): readonly Nomination[] {
  return row.nominations ?? (row.nomination ? [row.nomination] : []);
}

export class AttendeeMerger {
  /** Insertion order is display order. */
  private people = new Map<number, Person>();
  /** "C:<contactId>" / "L:<leadId>" → seq. */
  private byKey = new Map<string, number>();
  private seq = 0;

  addPage(rows: readonly EventAttendee[]): void {
    for (const row of rows) {
      if (listedMemberStatus(row.campaignMemberStatus)) this.add(row);
    }
  }

  private add(row: EventAttendee): void {
    const keys = [
      row.contactId ? `C:${row.contactId}` : undefined,
      row.leadId ? `L:${row.leadId}` : undefined,
    ].filter((k): k is string => !!k);

    // Every person this row links to, earliest first.
    const hits = [
      ...new Set(keys.map((k) => this.byKey.get(k)).filter((s): s is number => s !== undefined)),
    ].sort((a, b) => a - b);

    let person: Person;
    if (hits.length === 0) {
      person = {
        seq: this.seq++,
        row,
        keys: new Set(),
        memberships: new Map(),
        nominations: new Map(),
      };
      this.people.set(person.seq, person);
    } else {
      // The earliest keeps its fields and its place; the row joins people separate until now.
      person = this.people.get(hits[0])!;
      for (const s of hits.slice(1)) {
        const other = this.people.get(s)!;
        other.memberships.forEach((m, id) => person.memberships.set(id, m));
        other.nominations.forEach((n, id) => person.nominations.set(id, n));
        other.keys.forEach((k) => {
          person.keys.add(k);
          this.byKey.set(k, person.seq);
        });
        this.people.delete(s);
      }
    }
    for (const k of keys) {
      person.keys.add(k);
      this.byKey.set(k, person.seq);
    }
    (row.memberships ?? []).forEach((m) => {
      if (m.campaignMemberId && listedMemberStatus(m.campaignMemberStatus)) {
        person.memberships.set(m.campaignMemberId, m);
      }
    });
    rowNominations(row).forEach((n) => {
      if (n.nominationId) person.nominations.set(n.nominationId, n);
    });
  }

  /**
   * The people so far, in display order, each as one row: the first row's person fields, every
   * membership and nomination, and the preferred nomination recomputed over them.
   *
   * `campaignMemberStatus` is the person's across every membership: Attended when any is, else
   * Registered — not just the first row's, which may be a sub-campaign's Registered while another
   * says Attended. Its timestamp is that membership's. Without memberships (a service from before
   * the change) the row's own values stand.
   */
  rows(): EventAttendee[] {
    return [...this.people.values()].map(({ row, memberships, nominations }) => {
      const ms = [...memberships.values()];
      const ns = [...nominations.values()];
      const merged: EventAttendee = {
        ...row,
        memberships: ms,
        nominations: ns,
        nomination: preferredNomination(ns),
      };
      if (ms.length) {
        const shown =
          ms.find((m) => listedMemberStatus(m.campaignMemberStatus) === 'Attended') ?? ms[0];
        merged.campaignMemberStatus = listedMemberStatus(shown.campaignMemberStatus);
        merged.campaignMemberStatusUpdatedAt = shown.campaignMemberStatusUpdatedAt;
      }
      return merged;
    });
  }
}

function statusRank(n: Nomination): number {
  if (n.status === 'APPROVED') return 2;
  if (n.status === 'PENDING_REVIEW') return 1;
  return 0;
}

function createdAtMs(n: Nomination): number {
  const t = n.createdAt ? new Date(n.createdAt).getTime() : NaN;
  return Number.isNaN(t) ? -Infinity : t;
}

/**
 * The nomination the "nomination" columns show, by the service's rule (EventAttendeeService
 * `PREFERRED_NOMINATION`): approved, then pending review, then rejected; the newest `createdAt`
 * breaks ties, a missing one counting as oldest.
 */
export function preferredNomination(nominations: readonly Nomination[]): Nomination | undefined {
  let best: Nomination | undefined;
  for (const n of nominations) {
    if (
      !best ||
      statusRank(n) > statusRank(best) ||
      (statusRank(n) === statusRank(best) && createdAtMs(n) > createdAtMs(best))
    ) {
      best = n;
    }
  }
  return best;
}

/**
 * The first page's nominations no person carries. Final only once the last page has loaded: one
 * unmatched now may match a member on a later page.
 */
export function unmatchedNominations(
  firstPageNominations: readonly Nomination[],
  people: readonly EventAttendee[],
): Nomination[] {
  const matched = new Set(
    people.flatMap((p) => rowNominations(p).map((n) => n.nominationId)),
  );
  return firstPageNominations.filter((n) => !matched.has(n.nominationId));
}

/** A 400 from ListEventAttendees: on a later page, the hierarchy changed under the token. */
function isValidationError(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false;
  const err = e as { name?: string; $metadata?: { httpStatusCode?: number } };
  return err.name === 'ValidationException' || err.$metadata?.httpStatusCode === 400;
}

export type AttendeePageFetcher = (nextToken: string | undefined) => Promise<{
  attendees: EventAttendee[];
  nominations?: Nomination[];
  nextToken?: string;
}>;

export interface AttendeeSnapshot {
  /** The people so far, merged (see {@link AttendeeMerger.rows}). */
  people: EventAttendee[];
  /** The first page's `nominations`: every nomination the caller may see. */
  nominations: readonly Nomination[];
  /** The last page has loaded: counts, totals and unmatched nominations are final. */
  done: boolean;
}

/**
 * Walks ListEventAttendees to the end, handing `onPage` the merged state after each page.
 *
 *   - Keeps going while there is a `nextToken`, through empty pages: the service returns one as it
 *     finishes a campaign and moves to the next, so an empty page does not mean done.
 *   - Only the first page (no token) carries `nominations`; it is kept from there.
 *   - A 400 on a later page means the hierarchy changed while paging (a sub-campaign removed):
 *     everything is thrown away and the walk starts again from page 1, at most `maxRestarts`
 *     times. A 400 on page 1 is a real error and is thrown.
 *   - Stops quietly when `signal` aborts. `maxPages` bounds each walk, so a service that kept
 *     handing out tokens could not keep it going forever; it resolves `truncated` then.
 */
export async function loadMergedAttendees(
  fetchPage: AttendeePageFetcher,
  onPage: (snapshot: AttendeeSnapshot) => void,
  signal?: AbortSignal,
  { maxPages = 500, maxRestarts = 2 }: { maxPages?: number; maxRestarts?: number } = {},
): Promise<{ truncated: boolean }> {
  for (let restarts = 0; ; restarts++) {
    const merger = new AttendeeMerger();
    let nominations: readonly Nomination[] = [];
    let nextToken: string | undefined;
    let restart = false;
    for (let page = 0; page < maxPages; page++) {
      let result: Awaited<ReturnType<AttendeePageFetcher>>;
      try {
        result = await fetchPage(nextToken);
      } catch (e) {
        if (signal?.aborted) return { truncated: true };
        if (nextToken && restarts < maxRestarts && isValidationError(e)) {
          restart = true;
          break;
        }
        throw e;
      }
      if (signal?.aborted) return { truncated: true };
      if (!nextToken) nominations = result.nominations ?? [];
      merger.addPage(result.attendees);
      nextToken = result.nextToken;
      onPage({ people: merger.rows(), nominations, done: !nextToken });
      if (!nextToken) return { truncated: false };
    }
    if (!restart) return { truncated: true };
  }
}
